"use client";
import { useState, useEffect } from "react";
import { useLanguage } from '../../hooks/useLanguage';
import { Share2 } from "lucide-react";
import { Button } from "../ui/button";
import config from "../../config/config";

export default function SocialStreamSection() {
  const [settings, setSettings] = useState(null);
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [dimensions, setDimensions] = useState({ width: 340, height: 500 });
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  // Fetch social stream settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/social-stream?language=${language}`);
        const result = await response.json();
        
        if (result.success && result.data) {
          setSettings(result.data.socialStream);
          setLoading(false);
        } else {
          // Fallback if API fails - set as disabled
          setSettings({ enabled: false });
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching social stream settings:', error);
        setSettings({ enabled: false });
        setLoading(false);
      }
    };

    fetchSettings();
  }, [language]);

  // Initialize URLs when settings are loaded
  useEffect(() => {
    if (settings) {
      const width = settings.width || 340;
      const height = settings.height || 800; // Increased default height for better display
      const showFaces = settings.showFaces !== false;
      
      // Facebook URL
      if (settings.facebookUrl) {
        const encodedFbUrl = encodeURIComponent(settings.facebookUrl);
        const fbPluginUrl = `https://www.facebook.com/plugins/page.php?href=${encodedFbUrl}&tabs=timeline&width=${width}&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=${showFaces}&appId`;
        setFacebookUrl(fbPluginUrl);
      }
      
      // Instagram URL - Using Instagram embed
      if (settings.instagramUrl) {
        // Clean the URL and extract username properly
        const cleanUrl = settings.instagramUrl.replace(/\/$/, ''); // Remove trailing slash
        const instagramUsername = cleanUrl.split('/').pop().replace('@', '');
        const instagramEmbedUrl = `https://www.instagram.com/${instagramUsername}/embed/`;
        setInstagramUrl(instagramEmbedUrl);
      }
      
      setDimensions({ width, height });
      setLoading(false);
    }
  }, [settings]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] ? field[language] : (field.en ? field.en : '');
    }
    return '';
  };

  if (loading) {
    return null; // Don't show loading state to avoid layout shift
  }

  // Don't render if section is disabled or no URLs configured
  if (!settings?.enabled || (!settings?.facebookUrl && !settings?.instagramUrl)) {
    return null;
  }

  return (
    <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6" style={{ backgroundColor: '#ffffff' }}>
      <div className="max-w-7xl mx-auto w-full">
        {/* Social Feeds Grid */}
        <div className="flex flex-col lg:flex-row gap-1 sm:gap-1 md:gap-6">
          {/* Facebook Feed - 30% width on large devices */}
          {facebookUrl && (
            <div className="w-full lg:w-[30%]">
              <div className="flex flex-col min-h-[450px] max-h-[550px] sm:min-h-[500px] sm:max-h-[600px] md:min-h-[700px] md:max-h-[800px] lg:h-[600px] xl:h-[650px]">
                {/* Facebook Header */}
                <div className="p-1 lg:pt-0 lg:px-1 lg:pb-1 flex-shrink-0">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                    {/* Left side - Title and Description */}
                    <div className="mb-3 lg:mb-0">
                      <h3 className="text-lg font-bold mb-2 truncate">
                        {getLabel(settings?.facebookTitle) || 'Facebook'}
                      </h3>
                      {getLabel(settings?.facebookDescription) && (
                        <p className="text-sm opacity-80 line-clamp-2">
                          {getLabel(settings?.facebookDescription)}
                        </p>
                      )}
                    </div>
                    
                    {/* Right side - Button - Hidden on large devices */}
                    {getLabel(settings?.facebookButtonText) && settings?.facebookUrl && (
                      <div className="w-full lg:hidden lg:ml-4">
                        <Button 
                          size="sm" 
                          onClick={() => {
                            const target = settings?.facebookButtonTarget || '_blank';
                            if (target === '_blank') {
                              window.open(settings.facebookUrl, '_blank');
                            } else {
                              window.location.href = settings.facebookUrl;
                            }
                          }}
                          className="text-xs truncate w-full lg:w-auto"
                        >
                          {getLabel(settings?.facebookButtonText)}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Facebook Content */}
                <div className="flex-1 overflow-hidden flex items-center justify-center">
                  <div className="p-1 h-full w-full flex items-center justify-center">
                    <iframe
                      key={facebookUrl}
                      src={facebookUrl}
                      width="100%"
                      height="100%"
                      style={{ border: "none", overflow: "auto" }}
                      scrolling="yes"
                      frameBorder="0"
                      allowtransparency="true"
                      allow="encrypted-media"
                      title="Facebook Page"
                      className="w-full h-full min-h-[400px] ml-1.5"
                    />
                  </div>
                </div>
                
                {/* Full width button for large devices - below embed */}
                {getLabel(settings?.facebookButtonText) && settings?.facebookUrl && (
                  <div className="hidden lg:block px-1 pt-1 flex-shrink-0">
                    <Button 
                      size="sm" 
                      onClick={() => {
                        const target = settings?.facebookButtonTarget || '_blank';
                        if (target === '_blank') {
                          window.open(settings.facebookUrl, '_blank');
                        } else {
                          window.location.href = settings.facebookUrl;
                        }
                      }}
                      className="text-xs truncate w-full"
                    >
                      {getLabel(settings?.facebookButtonText)}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Instagram Feed - 70% width on large devices */}
          {instagramUrl && (
            <div className={`w-full mt-6 sm:mt-6 lg:mt-0 ${facebookUrl ? 'lg:w-[70%]' : 'lg:w-full'}`}>
              <div className="flex flex-col min-h-[450px] max-h-[550px] sm:min-h-[500px] sm:max-h-[600px] md:min-h-[700px] md:max-h-[800px] lg:h-[600px] xl:h-[650px] border border-gray-200">
                {/* Instagram Header */}
                <div className="p-3 lg:p-4 flex-shrink-0">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                    {/* Left side - Title and Description */}
                    <div className="mb-3 lg:mb-0">
                      <h3 className="text-lg font-bold mb-2 truncate">
                        {getLabel(settings?.instagramTitle) || 'Instagram'}
                      </h3>
                      {getLabel(settings?.instagramDescription) && (
                        <p className="text-sm opacity-80 line-clamp-2">
                          {getLabel(settings?.instagramDescription)}
                        </p>
                      )}
                    </div>
                    
                    {/* Right side - Button */}
                    {getLabel(settings?.instagramButtonText) && settings?.instagramUrl && (
                      <div className="w-full lg:w-auto lg:ml-4">
                        <Button 
                          size="sm" 
                          onClick={() => {
                            const target = settings?.instagramButtonTarget || '_blank';
                            if (target === '_blank') {
                              window.open(settings.instagramUrl, '_blank');
                            } else {
                              window.location.href = settings.instagramUrl;
                            }
                          }}
                          className="text-xs truncate w-full lg:w-auto"
                        >
                          {getLabel(settings?.instagramButtonText)}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {/* Instagram Content */}
                <div className="flex-1 overflow-hidden">
                  <div className="p-2 lg:p-3 h-full">
                    <iframe
                      key={instagramUrl}
                      src={instagramUrl}
                      width="100%"
                      height="100%"
                      style={{ border: "none", overflow: "auto" }}
                      scrolling="yes"
                      frameBorder="0"
                      allowtransparency="true"
                      title="Instagram Profile"
                      className="w-full h-full min-h-[400px]"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}